import React, { useCallback, useMemo, useRef, useState } from 'react';
import Loader from '../../../loaders';
import { FiltersConverter, SortsConverter } from '../../../../helpers';
import api from "../../../../services/api";
import { ButtonGroup, Col, Row, UncontrolledTooltip } from 'reactstrap';
import { TooltipButton } from '../../../inputs';
import { AgGridReact } from 'ag-grid-react';
import qs from "qs";
import ConfirmGeneralAlert from '../../../alerts/ConfirmGeneralAlert';
import { ErrorNotification } from '../../../alerts';
import fileDownload from 'js-file-download';
import moment from 'moment';

const pageSize = 50;
const apiUrl = "employeechangeconditionv2";

export default function ECCList() {
    const [loading, setLoading] = useState(false);
    const [popupMessageObj, setPopupMessageObj] = useState(null);
    const loadingOverlayComponent = useMemo(() => Loader, []);
    const gridRef = useRef();

    const defaultColDef = useMemo(() => ({
        sortable: true,
        resizable: true,
        filter: true,
        editable: false,
        filter: 'agTextColumnFilter',
        filterParams: {
            buttons: ['reset'],
            debounceMs: 200,
            excelMode: 'mac',
        },
        floatingFilter: true,
        floatingFilterComponentParams: {
            suppressFilterButton: true,
        },
        suppressMenu: true,
    }), []);

    const getServerSideDatasource = useCallback(() => {
        return {
            getRows: (params) => {
                const { request } = params;
                const { filterModel, sortModel } = request;

                const currentFilters = FiltersConverter(filterModel);
                const currentSorts = SortsConverter(sortModel);

                const queryString = qs.stringify({ filters: currentFilters, sortBy: currentSorts }, { allowDots: true });
                const url = `/${apiUrl}/${params.request.startRow ?? 0}/${pageSize}${params.requestId ? ("/" + params.requestId + "/" + params.action) : ""}${queryString ? `?${queryString}` : ""}`;

                api.get(url)
                    .then((response) => {
                        // console.log({response});
                        const successData = {
                            rowData: response.data.data,
                            rowCount: response.data.total,
                        };

                        params.success(successData);
                    })
                    .catch(error => {
                        params.fail();
                        console.error({error});
                    });
            },
        };
    }, [pageSize]);

    const onGridReady = useCallback((params) => {
        params.api.setServerSideDatasource(getServerSideDatasource());
        params.api.sizeColumnsToFit();
        window.addEventListener('resize', function () {
            setTimeout(function () {
                params.api.sizeColumnsToFit();
            });
        });
    }, [getServerSideDatasource]);

    const generateApproval = useCallback((id) => {
        if (!id || !gridRef?.current)
            return;

        setLoading(true);

        api.post(`${apiUrl}/approval/generate/${id}`)
            .then(() => {
                if (popupMessageObj)
                    setPopupMessageObj(null);

                setLoading(false);
                onGridReady(gridRef.current);
            })
            .catch((error) => {
                console.error({error});
                setPopupMessageObj({
                    type: "error",
                    message: `${error.response.data}`,
                });
                setLoading(false);
            })
        ;
    }, [gridRef, popupMessageObj, onGridReady]);

    const finalise = useCallback((id) => {
        if (!id || !gridRef?.current)
            return;

        setLoading(true);

        api.post(`${apiUrl}/finalise/${id}`)
            .then(() => {
                if (popupMessageObj)
                    setPopupMessageObj(null);

                setLoading(false);
                onGridReady(gridRef.current);
            })
            .catch((error) => {
                console.error({error});
                setPopupMessageObj({
                    type: "error",
                    message: `${error.response.data}`,
                });
                setLoading(false);
            })
        ;
    }, [gridRef, popupMessageObj, onGridReady]);

    const downloadDocument = useCallback((id) => {
        setLoading(true);

        api.get(`${apiUrl}/document/${id}`, { responseType: 'blob' })
            .then(blob => {
                const filename = /(?<=attachment;.*filename=").*(?=";)/.exec(blob.headers["content-disposition"])[0]
                fileDownload(blob.data, filename);
            })
            .catch(error => {
                console.error(error)
            })
            .finally(() => setLoading(false))
        ;
    }, []);

    const columnDefs = [
        {
            field: "employeeChangeConditionNumber",
            headerName: "ECC Number",
            sort: "asc",
            minWidth: 150,
        },
        {
            field: "candidateName",
            headerName: "Employee Name",
            colId: "candidate.candidateName",
            minWidth: 150,
        },
        {
            field: "projectName",
            headerName: "Project",
            minWidth: 150,
        },
        {
            field: "proposedRecruitmentTypeName",
            headerName: "Proposed Recruitment Type",
            minWidth: 150,
        },
        {
            field: "proposedEmploymentTypeName",
            headerName: "Proposed Employment Basis",
            minWidth: 150,
        },
        {
            field: "createdBy",
            headerName: "Requested By",
            minWidth: 150,
        },
        {
            field: "proposedPointOfHireName",
            headerName: "Proposed Point of Hire",
            minWidth: 150,
        },
        {
            field: "statusName",
            headerName: "Status",
            minWidth: 150,
        },
        {
            field: "approverName",
            headerName: "Next Approver",
            minWidth: 150,
        },
        {
            field: "actions",
            headerName: "",
            filter: false,
            maxWidth: 90,
            minWidth: 90,
            pinned: "right",
            cellRenderer: (row) => {
                const {data: rowData} = row;
                const canEdit = rowData.statusName === "Draft";
                const canApprove = rowData.statusName === "Draft" || rowData.statusName === "Ready";
                const readyToFinalise = rowData.statusName === "Approved";
                const finalised = rowData.statusName === "Final";
                const approvalButtonId = `ecc_generate_approval_${rowData.employeeChangeConditionId}`;
                
                return (
                    <div key={`ecc_action_${rowData.employeeChangeConditionId}`} id={`ecc_action_${rowData.employeeChangeConditionId}`} className="text-center">
                        <TooltipButton
                            id={`ecc_edit_${rowData.employeeChangeConditionId}`}
                            title={canEdit ? "Edit" : "View"}
                            className="btn-icon"
                            color="default"
                            size="xss"
                            type="button"
                            onClick={() => window.open(`/eccform/${rowData.employeeChangeConditionId}`, '_blank')}
                        >
                            <i className={`fa fa-xs ${canEdit ? "fa-pencil-alt" : "fa-search"} pt-1`}></i>
                        </TooltipButton>
                    {
                        !finalised ?
                        <TooltipButton
                            id={approvalButtonId}
                            title={readyToFinalise || !canApprove ? "Finalise" : "Send for Approval"}
                            className="btn-icon"
                            color="default"
                            size="xss"
                            type="button"
                            onClick={() => {
                                setPopupMessageObj({
                                    type: readyToFinalise ? "finalise" : "confirmation",
                                    data: rowData.employeeChangeConditionId,
                                    message: readyToFinalise ? "Finalise this request?" : "Are you sure you want to submit this request for approval?",
                                });
                            }}
                            disabled={!canApprove && !readyToFinalise}
                        >
                            <i className={`fa fa-xs fa-${(readyToFinalise || !canApprove) ? "check" : "step-forward"} pt-1`}></i>
                        </TooltipButton>
                        :
                        null
                    }
                    {
                        finalised ?
                        <>
                            <div className="btn btn-icon btn btn-outline-success btn-xss"><span id={approvalButtonId}><i className="fa fa-xs fa-check pt-1"></i></span></div>
                            <UncontrolledTooltip delay={0} placement="top" target={approvalButtonId}>
                                <div className="text-left w-100 d-flex flex-wrap justify-content-start align-items-center">
                                    <Row className="m-0 p-0 w-100">
                                        <Col className="m-0 p-0 text-center">
                                            <span>Finalised</span>
                                        </Col>
                                    </Row>
                                    <Row className="m-0 p-0 mt-2 w-100">
                                        <Col xs={2} className="m-0 p-0">
                                            By:
                                        </Col>
                                        <Col xs={10} className="m-0 p-0">
                                            {rowData?.modifiedBy ?? "N/A"}
                                        </Col>
                                    </Row>
                                    <Row className="m-0 p-0 mt-2 w-100">
                                        <Col xs={2} className="m-0 p-0">
                                            At:
                                        </Col>
                                        <Col xs={10} className="m-0 p-0">
                                            {rowData?.modifiedDate ? moment(rowData?.modifiedDate).format("DD MMM YYYY HH:mm") : "N/A"}
                                        </Col>
                                    </Row>
                                </div>
                            </UncontrolledTooltip>
                        </>
                        :
                        null
                    }
                    </div>
                );
            }
        },
    ];

    return (
        <>
            {loading && <Loader />}
            <Row>
                <Col sm={12} className="d-flex flex-wrap justify-content-start align-items-center mb-2">
                    <TooltipButton
                        id="addnew-eccf"
                        title="Add New ECCF"
                        className="btn-icon"
                        color="default"
                        size="sm"
                        type="button"
                        onClick={() => window.open("/eccinitialform", '_blank')}
                    >
                        <i className="fas fa-plus pt-1"></i>
                    </TooltipButton>
                    <TooltipButton
                        id="export-excel-eccf"
                        title="Export ECCF to Excel"
                        className="btn-icon"
                        color="default"
                        size="sm"
                        type="button"
                        onClick={() => {}}
                    >
                        <i className="fas fa-file-excel pt-1"></i>
                    </TooltipButton>
                </Col>
            </Row>
            <Row>
                <Col sm={12}>
                    <div className="ag-theme-alpine ag-grid" style={{ width: '100%', height: 'calc(100vh - 200px)' }}>
                        <AgGridReact
                            ref={gridRef}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            loadingOverlayComponent={loadingOverlayComponent}
                            suppressColumnVirtualisation={false}
                            suppressRowVirtualisation={false}
                            debounceVerticalScrollbar={false}
                            rowBuffer={20}
                            headerHeight={30}
                            groupHeaderHeight={30}
                            rowHeight={30}
                            floatingFiltersHeight={30}
                            suppressDragLeaveHidesColumns={true}
                            suppressColumnMoveAnimation={true}
                            animateRows={false}
                            pagination={true}
                            paginationPageSize={pageSize}
                            cacheBlockSize={pageSize}
                            rowModelType={'serverSide'}
                            serverSideInfiniteScroll={true}
                            onGridReady={onGridReady}
                            rowClass={'ag-row-normal'}
                        />
                    </div>
                </Col>
            </Row>
        {
            popupMessageObj?.type === "confirmation" ?
            <ConfirmGeneralAlert
                onConfirm={() => generateApproval(popupMessageObj.data)}
                onCancel={() => setPopupMessageObj(null)}
                textConfirm="Yes"
                title="ECC Submit for Approval Confirmation"
                textDesc={popupMessageObj.message}
            />
            :
            null
        }
        {
            popupMessageObj?.type === "finalise" ?
            <ConfirmGeneralAlert
                onConfirm={() => finalise(popupMessageObj?.data)}
                onCancel={() => setPopupMessageObj(null)}
                textConfirm="Yes"
                title="ECC Finalisation Confirmation"
                textDesc={popupMessageObj.message}
            />
            :
            null
        }
        {
            popupMessageObj?.type === "error" ?
            <ErrorNotification
                onConfirm={() => setPopupMessageObj(null)}
                message={popupMessageObj.message}
            />
            :
            null
        }
        </>
    );
};